import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { 
  Box, 
  TextField, 
  Grid,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  InputAdornment,
  CircularProgress,
  Autocomplete,
  FormControlLabel,
  Checkbox,
  Tooltip
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Key from '../../clientVariables.json';

const JobSearchBar = ({ onSearch }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedStateId, setSelectedStateId] = useState('');
  const [selectedCityId, setSelectedCityId] = useState('');
  const [selectedStateName, setSelectedStateName] = useState('');
  const [selectedCityName, setSelectedCityName] = useState('');
  const [stateSearchText, setStateSearchText] = useState('');
  const [citySearchText, setCitySearchText] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [isLoadingStates, setIsLoadingStates] = useState(false);
  const [isLoadingCities, setIsLoadingCities] = useState(false);  const [selectedDate, setSelectedDate] = useState(null);
  const [exactMatch, setExactMatch] = useState(false);

  // Fetch US states on component mount
  useEffect(() => {
    fetchStates();
  }, []);

  // Function to fetch US states (country_id 233 is for US)
  const fetchStates = () => {
    setIsLoadingStates(true);
    axios
      .post(`${Key.domain_jnp}/commonAPIs/get_states_by_countryid`, {
        country_id: 233 // US Country ID
      })
      .then((response) => {
        console.log("States API response:", response.data.data);
        
        // Map states from API response
        const stateOptions = response.data.data.map((state) => ({
          value: state.id,
          label: state.name
        }));
        
        setStates(stateOptions);
        setIsLoadingStates(false);
      })
      .catch(error => {
        console.error("Error fetching states:", error);
        setIsLoadingStates(false);
      });
  };

  // Function to fetch cities for a selected state
  const fetchCities = (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    
    setIsLoadingCities(true);
    axios
      .post(`${Key.domain_jnp}/commonAPIs/get_cities_by_stateid`, {
        state_id: stateId
      })
      .then((response) => {
        console.log("City API response:", response.data.data);
        
        // Map cities from API response
        const cityOptions = response.data.data.map((city) => ({
          value: city.id,
          label: city.cityName
        }));
        
        setCities(cityOptions);
        setIsLoadingCities(false);
      })
      .catch(error => {
        console.error("Error fetching cities:", error);
        setIsLoadingCities(false);
        setCities([]);
      });
  };

  const handleStateChange = (event) => {
    const stateId = event.target.value;
    setSelectedStateId(stateId);
    setSelectedCityId('');
    setSelectedCityName('');
    
    // Find the state name from the id for sending in search
    const selectedState = states.find(state => state.value === stateId);
    setSelectedStateName(selectedState ? selectedState.label : '');
    
    // Fetch cities for the selected state
    if (stateId) {
      fetchCities(stateId);
    } else {
      setCities([]);
    }
  };

  const handleCityChange = (event) => {
    const cityId = event.target.value;
    setSelectedCityId(cityId);
    
    // Find the city name from the id for sending in search
    const selectedCity = cities.find(city => city.value === cityId);
    setSelectedCityName(selectedCity ? selectedCity.label : '');
  };  const handleSearch = () => {
    // Only show a message about exact match if there's actually a search query
    if (exactMatch && searchQuery.trim()) {
      console.log(`Using exact match for search term: "${searchQuery}"`);
      // Add visual cue that exact matching is active
      document.title = `JNP - Exact Match: "${searchQuery}"`;
    }
    
    onSearch({
      searchQuery,
      state: selectedStateName,
      city: selectedCityName,
      fromDate,
      toDate,
      // Use the exactMatch state from the checkbox
      exactTitleMatch: exactMatch
    });
  };  const handleClear = () => {
    setSearchQuery('');
    setSelectedStateId('');
    setSelectedCityId('');
    setSelectedStateName('');
    setSelectedCityName('');
    setStateSearchText('');
    setCitySearchText('');
    setCities([]);
    setFromDate(null);
    setToDate(null);
    setExactMatch(false);
    
    // Reset the page title
    document.title = 'JNP - Job Validation';
    
    onSearch({});
  };  return (
    <Box component="form" noValidate sx={{ width: '100%' }}>
      <Grid container spacing={{ xs: 0.5, sm: 1 }} alignItems="flex-start">
        <Grid item xs={12}>
          <Box sx={{ mb: 1 }}>            <TextField
              fullWidth
              label={exactMatch ? "Search jobs by EXACT title match" : "Search jobs (title, company, skills, etc.)"}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={exactMatch ? "E.g. React Native Developer (exact match only)" : "E.g. React Developer, JavaScript, Google"}
              variant="outlined"
              size="small"
              InputProps={{
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
                sx: exactMatch ? { 
                  '& .MuiOutlinedInput-notchedOutline': { 
                    borderColor: 'primary.main',
                    borderWidth: '2px'
                  } 
                } : {}
              }}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              sx={{
                '& .MuiInputLabel-root': exactMatch ? {
                  color: 'primary.main',
                  fontWeight: 'bold'
                } : {}
              }}
            />            {/* We'll remove this box as we'll move the checkbox to the same row as buttons */}</Box>
        </Grid>
          <Grid item xs={12} sm={4} md={2} lg={2}>
          <Autocomplete
            id="state-search-select"
            options={states}
            getOptionLabel={(option) => option.label || ''}
            value={selectedStateId ? states.find(state => state.value === selectedStateId) || null : null}
            onChange={(event, newValue) => {
              setSelectedStateId(newValue ? newValue.value : '');
              setSelectedStateName(newValue ? newValue.label : '');
              setSelectedCityId('');
              setSelectedCityName('');
              if (newValue && newValue.value) {
                fetchCities(newValue.value);
              } else {
                setCities([]);
              }
            }}
            inputValue={stateSearchText}
            onInputChange={(event, newInputValue) => {
              setStateSearchText(newInputValue);
            }}
            loading={isLoadingStates}
            disabled={isLoadingStates}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="State"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {isLoadingStates ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            noOptionsText="No states found"
          />
        </Grid>
        
        <Grid item xs={12} sm={4} md={2} lg={2}>          <Autocomplete
            id="city-search-select"
            options={cities}
            getOptionLabel={(option) => option.label || ''}
            value={selectedCityId ? cities.find(city => city.value === selectedCityId) || null : null}
            onChange={(event, newValue) => {
              setSelectedCityId(newValue ? newValue.value : '');
              setSelectedCityName(newValue ? newValue.label : '');
            }}
            inputValue={citySearchText}
            onInputChange={(event, newInputValue) => {
              setCitySearchText(newInputValue);
            }}
            loading={isLoadingCities}
            disabled={!selectedStateId || isLoadingCities}
            size="small"
            renderInput={(params) => (
              <TextField
                {...params}
                label="City"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      {isLoadingCities ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.startAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.value}>
                {option.label}
              </li>
            )}
            noOptionsText="No cities found"
          />
        </Grid>
        
        <Grid item xs={6} sm={4} md={2} lg={2}>          <LocalizationProvider dateAdapter={AdapterDateFns}>            <DatePicker
              label="From Date"
              value={fromDate}
              onChange={(newValue) => setFromDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              inputFormat="MM/dd/yyyy"
              PopperProps={{
                sx: { 
                  '& .MuiPaper-root': {
                    transformOrigin: 'top center !important',
                    '&:before': { display: 'none' } 
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
          <Grid item xs={6} sm={4} md={2} lg={2}>
          <LocalizationProvider dateAdapter={AdapterDateFns}><DatePicker
              label="To Date"
              value={toDate}
              onChange={(newValue) => setToDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth size="small" />}
              inputFormat="MM/dd/yyyy"
              PopperProps={{
                sx: { 
                  '& .MuiPaper-root': {
                    transformOrigin: 'top center !important',
                    '&:before': { display: 'none' } 
                  }
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
          <Grid item xs={12} sx={{ mt: { xs: 1, sm: 1 } }}>          <Grid container spacing={1} alignItems="center" justifyContent="space-between" sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
            <Grid item xs={12} sm="auto" sx={{ mb: { xs: 1, sm: 0 } }}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center'
              }}>
                <Tooltip title="When enabled, search will only return exact matches for job titles">
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={exactMatch} 
                        onChange={(e) => setExactMatch(e.target.checked)} 
                        size="small"
                        color="primary"
                        sx={{ p: 0.5 }}
                      />
                    }
                    label={
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        flexWrap: 'nowrap'
                      }}>
                        <span style={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}>Exact Match</span>
                        {exactMatch && (
                          <Box component="span" sx={{ 
                            ml: 0.5, 
                            px: 0.5, 
                            py: 0.1, 
                            bgcolor: 'primary.main', 
                            color: 'white', 
                            borderRadius: 1, 
                            fontSize: '0.65rem',
                            whiteSpace: 'nowrap'
                          }}>
                            Active
                          </Box>
                        )}
                      </Box>
                    }
                    sx={{ 
                      mr: 0,
                      '.MuiFormControlLabel-label': {
                        fontSize: { xs: '0.75rem', sm: '0.8rem' }
                      }
                    }}
                  />
                </Tooltip>
              </Box>
            </Grid>
              <Grid item xs={12} sm="auto">
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                width: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'flex-end', sm: 'flex-end' }
              }}>
                <Button
                  variant="outlined"
                  onClick={handleClear}
                  sx={{ 
                    px: 3,
                    height: '40px',
                    minWidth: '90px',
                    textTransform: 'none',
                    fontWeight: 'normal',
                    borderRadius: '4px',
                    backgroundColor: '#f8f9fa',
                    borderColor: '#ced4da'
                  }}
                >
                  Clear
                </Button>
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSearch}
                  sx={{ 
                    px: 3,
                    height: '40px',
                    minWidth: '90px',
                    textTransform: 'none',
                    fontWeight: 'normal',
                    borderRadius: '4px'
                  }}
                >
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

JobSearchBar.propTypes = {
  onSearch: PropTypes.func.isRequired
};

export default JobSearchBar;
